<template>
  <b-container :id="'msg_' + id" :class="classes" fluid>
    <b-row class="px-3 py-2">
      <!-- タイトル -->
      <div
        ref="message-title"
        class="message-title"
        v-html="$sanitize(titleLabel, { allowedAttributes: { span: ['class'] } })"
      ></div>
      <!-- ツールチップ(タイトル省略時のみ表示) -->
      <b-tooltip
        @show="onTitleTooltipShow"
        :target="() => $refs['message-title']"
        :title="titleLabel"
      />

      <div class="d-flex ml-auto">
        <!-- URLコピー＆編集/削除メニュー -->
        <div>
          <b-iconstack
            v-if="!search"
            :id="'edit-button-' + id"
            class="three-dots"
            font-scale="1"
            tabindex="0"
            ><b-icon-three-dots></b-icon-three-dots
          ></b-iconstack>
          <b-popover
            :target="'edit-button-' + id"
            ref="popover"
            placement="bottom"
            triggers="focus"
          >
            <div>
              <div class="edit-menu-item" v-on:click="onUrlCopyMessage">URLコピー</div>
              <div v-if="reactionAvailable" class="edit-menu-item" v-on:click="showFavoriteList">
                いいね！の一覧を表示する
              </div>
              <div v-if="editable" class="edit-menu-item" v-on:click="onEditMessage">編集</div>
              <div v-if="deletable" class="edit-menu-item" v-on:click="onDeleteMessage">削除</div>
            </div>
          </b-popover>
        </div>
        <!-- ピン/お気に入り -->
        <div v-if="pinned" v-on:click="onStarClick"><icon-pinned class="star"></icon-pinned></div>
        <div v-else-if="starFlag && !search" v-on:click="onStarClick">
          <b-iconstack class="star" font-scale="1"
            ><b-icon-star-fill class="has-star"></b-icon-star-fill
          ></b-iconstack>
        </div>
        <div v-else-if="!search" v-on:click="onStarClick">
          <b-iconstack class="star" font-scale="1"
            ><b-icon-star-fill class="not-star"></b-icon-star-fill
          ></b-iconstack>
        </div>
      </div>
    </b-row>
    <b-row class="px-3">
      <!-- タイトル画像 -->
      <div class="p-auto">
        <profile-icon
          class="topic-list-item-icon rounded-circle m-auto"
          :userName="contributor.name"
          :id="contributorId"
          :image="contributorIcon"
          :size="25"
        />
      </div>
      <!-- 投稿者名 -->
      <div
        ref="contributor-el"
        class="contributor"
        v-html="$sanitize(contributor.name, { allowedAttributes: { span: ['class'] } })"
      ></div>
      <!-- ツールチップ(ユーザ名省略時のみ表示) -->
      <b-tooltip
        @show="onUserTooltipShow"
        :target="() => $refs['contributor-el']"
        :title="contributor.name"
      />

      <!-- 投稿日 -->
      <div class="time-stamp">{{ timeStamp }}</div>
    </b-row>
    <b-row class="px-3 pt-2">
      <!-- 投稿内容 -->
      <div class="content">
        <div
          class="mb-2"
          v-html="
            $sanitize(contents, {
              allowedAttributes: { span: ['class'], a: ['href', 'name', 'target', 'rel'] },
            })
          "
        />
        <!-- 投稿写真 -->
        <attachment-list :files="photos" :messageId="id" :commentId="''" />
      </div>
    </b-row>
    <b-row v-if="!search" class="px-3">
      <!-- ディバイダ -->
      <div class="text-devider"></div>
    </b-row>

    <b-row v-if="!search" class="d-flex px-3 py-2 justify-content-end" style="align-items: center">
      <!-- イイね！ -->
      <div v-if="reactionAvailable" class="d-inline-flex">
        <div class="like-stamp">
          <div v-if="likeFlag" class="liked" v-on:click="onLikeClick">
            <icon-like class="liked-icon"></icon-like>いいね！
          </div>
          <div v-else class="normal" v-on:click="onLikeClick">
            <icon-like class="normal-icon"></icon-like>いいね！
          </div>
        </div>
        <div :class="likeCount ? 'fav-link active' : 'fav-link'" v-on:click="showFavoriteList">
          {{ likeStamp }}
        </div>
        <div class="fav-link mx-1">|</div>
      </div>
      <!-- コメント数 -->
      <div class="comment-link" v-on:click="clickedLink">{{ commentLink }}</div>
      <!-- ソート順 -->
      <b-form-group class="my-auto">
        <b-form-radio-group
          v-model="sortValue"
          :options="[
            { text: '昇順', value: 'ASC' },
            { text: '降順', value: 'DESC' },
          ]"
          button-variant="outline-info"
          size="sm"
          buttons
        ></b-form-radio-group>
      </b-form-group>
    </b-row>
    <b-row>
      <comment-list
        v-show="showCommentList"
        :id="id"
        :comments="commentSource"
        :users="users"
        :viewer="viewer"
        :allow_attachment_type="allow_attachment_type"
        :sort="sortValue"
      />
    </b-row>
  </b-container>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  getCurrentInstance,
  onBeforeMount,
  ref,
  watch,
  type PropType,
} from "vue";
import Vue from "vue";
import { useAppStore, useCommentStore } from "@/store";
import "./message-list-item.scss";
import IconPinned from "./icon/IconPinned.vue";
import IconLike from "./icon/IconLike.vue";
import ProfileIcon from "./ProfileIcon.vue";
import { useTextProcess } from "@/composables/useTextProcess";
import { useClipboardCopy } from "@/composables/useClipboardCopy";
import { useLegacyRootEvent } from "@/composables/useLegacyRootEvent";
import { CommentListItemData } from "./CommentListItem.vue";
import CommentList from "./CommentList.vue";
import type { Attachment, IconReaction, FavoriteModalUser } from "../model";
import { User, Acl } from "../model";
import moment from "moment";
import sanitizeHTML from "../sanitize";
import { IconReactionType } from "../API";
import { ReactionController } from "../model/reaction-controller";
import { BPopover } from "bootstrap-vue";
import AclManager from "../model/acl-manager";
import { allowFeature } from "@/direct-app-config";
import { AttachmentFileTypesDefault, AttachmentFileTypesNone } from "@/suppport-attachment-types";
import type { SORT_TYPE } from "@/store";
import AttachmentList from "./attachment/AttachmentList.vue";

Vue.prototype.$sanitize = sanitizeHTML;

export type MessageListItemData = {
  id: string;
  title: string;
  pinned: boolean;
  icon: string;
  owner: string;
  updatedAt: Date;
  message: string;
  photos: string[];
  comments: CommentListItemData[];
  search?: string;

  star?: IconReaction; // ★
  like?: IconReaction; // イイね！
};

export default defineComponent({
  name: "message-list-item",
  components: {
    IconPinned,
    IconLike,
    CommentList,
    ProfileIcon,
    AttachmentList,
  },
  props: {
    id: { type: String, default: () => "", required: true },
    title: { type: String, default: () => "タイトル", required: true },
    pinned: { type: Boolean, default: () => false },
    icon: { type: String, default: () => "about:blank" },
    // cognito user-id
    owner: { type: String, default: () => "" },
    updatedAt: { type: Date as PropType<Date>, default: () => new Date(), required: true },
    message: { type: String, default: () => "" },
    photos: { type: Array as PropType<Attachment[]>, default: () => [] },
    comments: { type: Array as PropType<CommentListItemData[]>, default: () => [] },
    search: { type: String, default: () => "" },
    topicId: { type: String, required: true },
    baseSort: { type: String as PropType<SORT_TYPE>, default: () => "ASC" },
    // ユーザー情報
    users: { type: Array as PropType<User[]>, default: () => [] }, // 組織内ユーザー情報
    viewer: { type: Object as PropType<User>, default: () => User.createNotFoundUser() }, // ログインユーザ情報
    // Reaction関係
    star: { type: Object as PropType<IconReaction | undefined>, default: () => undefined },
    like: { type: Object as PropType<IconReaction | undefined>, default: () => undefined },
    // ファイル添付設定
    allow_attachment_type: {
      default: () => AttachmentFileTypesDefault,
    },
    acl: { type: Object as PropType<Acl>, default: () => Acl.createDummy() },
  },
  setup(props, { emit }) {
    // 旧 TextProcessMixin / ClipboardCopyMixin → composable（1-D 利用側書き換え）
    const { highLightStr } = useTextProcess();
    const { onUrlCopy } = useClipboardCopy();
    const rootEvent = useLegacyRootEvent();
    // テンプレート文字列 ref（$refs.popover / $refs["message-title"] / $refs["contributor"]）へ
    // アクセスするためのインスタンスプロキシ。テンプレート側の ref 名と衝突する computed
    // （contributor）があるため、setup の return には載せず $refs 経由で参照する。
    const vm = getCurrentInstance()?.proxy as Vue | undefined;

    const showCommentList = ref<boolean>(true); // 初期コメント[開]状態
    const sortValue = ref<SORT_TYPE>("ASC");

    const findUser = (ownerId: string): User => {
      const user = props.users.find((user) => user.id == ownerId);
      return user ? user : User.createNotFoundUser(ownerId);
    };

    const getMessage = () => {
      return useAppStore().getMessage(domainId.value, props.topicId, props.id);
    };

    // 添付可能か
    const allow_attachment = computed((): boolean => {
      return props.allow_attachment_type != AttachmentFileTypesNone;
    });

    const classes = computed((): { [selector: string]: boolean } => {
      return {
        "message-list-item": true,
      };
    });

    const commentSource = computed((): CommentListItemData[] => {
      const src = useCommentStore().get(props.topicId, props.id);
      return src || [];
    });

    const titleLabel = computed((): string => {
      return highLightStr(props.title, props.search);
    });

    // 作成者情報
    const contributor = computed((): User => {
      return findUser(props.owner);
    });
    const contributorId = computed((): string => {
      return contributor.value.directId;
    });
    const contributorIcon = computed((): string => {
      return contributor.value.getProfileIconUrl();
    });

    const timeStamp = computed((): string => {
      const dt = moment(props.updatedAt);
      return dt.format("YYYY/MM/DD HH:mm");
    });
    const contents = computed((): string => {
      return highLightStr(props.message, props.search);
    });
    const likeStamp = computed((): string => {
      return `${likeCount.value}人 `;
    });
    const commentLink = computed((): string => {
      const comments = commentSource.value.filter((comment) => !comment.deleted); // 削除済みは無視する
      return `コメント ${comments.length}件`;
    });

    const domainId = computed((): string => {
      return useAppStore().domainId || "";
    });
    const editable = computed((): boolean => {
      return (
        props.viewer.id == props.owner && AclManager.updateMessage(AclManager.store, getMessage())
      );
    });
    const deletable = computed((): boolean => {
      return (
        props.viewer.id == props.owner && AclManager.deleteMessage(AclManager.store, getMessage())
      );
    });

    // Reaction関係
    const starFlag = computed((): boolean => {
      return props.star?.userList.some((u) => u.userId == props.viewer.directId) || false;
    });
    const likeFlag = computed((): boolean => {
      return props.like?.userList.some((u) => u.userId == props.viewer.directId) || false;
    });
    const likeCount = computed((): number => {
      return props.like?.userList.length || 0;
    });

    // 権限
    const commentAvailable = computed((): boolean => {
      if (allowFeature("deny-edit") == false) return true; // 未対応組織なので許可
      return (
        AclManager.getCreateCommentAcl(AclManager.store, domainId.value, props.topicId, props.id) !=
        "deny"
      );
    });
    const reactionAvailable = computed((): boolean => {
      if (allowFeature("deny-edit") == false) return true; // 未対応組織なので許可
      return (
        AclManager.getCreateReactionAcl(
          AclManager.store,
          domainId.value,
          props.topicId,
          props.id
        ) != "deny"
      );
    });

    const openPhotoStream = (): void => {
      emit("openModal", props.id);
    };

    const onEditMessage = (): void => {
      emit("toEditMessage", props.id);
    };

    const onDeleteMessage = (): void => {
      emit("toDeleteMessage", props.id);
    };

    const onCommentLinkClick = (): void => {
      emit("onLinkClicked", props.id);
    };

    // Urlコピー
    const onUrlCopyMessage = (): void => {
      (vm?.$refs.popover as BPopover).$emit("close"); // Close
      const domainId = useAppStore().domainId;
      const topicId = useAppStore().topicId;
      onUrlCopy({ domainId: domainId, topicId: topicId, messageId: props.id, commentId: "" });
    };

    const onErrorModalEvent = (event: string): void => {
      rootEvent.emit(event);
    };

    // Reactionの設定処理
    const onReactionClicked = (reactionType: IconReactionType, currentValue: boolean) => {
      const ctl = new ReactionController();
      const msg = ctl.findMessage(props.id);
      ctl.onReactionClicked(msg, reactionType, props.viewer, currentValue);
    };

    // Reactionのクリック
    const onStarClick = (): void => {
      onReactionClicked(IconReactionType.FAVORITE, starFlag.value);
    };
    const onLikeClick = (): void => {
      onReactionClicked(IconReactionType.LIKE, likeFlag.value);
    };

    // コメント開閉
    const clickedLink = (): void => {
      showCommentList.value = !showCommentList.value;
    };

    // いいね一覧表示
    const showFavoriteList = (): void => {
      const userList = props.like?.userList || [];
      const favoriteUsers = userList.map((favoriteUser): FavoriteModalUser => {
        const user = props.users.find((u) => u.directId == favoriteUser.userId);
        return {
          user: user ? user : User.createNotFoundUser(favoriteUser.userId),
          updatedAt: favoriteUser.updatedAt,
        };
      });
      rootEvent.emit("on-favorite-modal", {
        userList: favoriteUsers,
        messageId: props.id,
        commentId: "",
      });
    };

    /* タイトル省略時のツールチップを出すかどうかを判定する */
    // タイトル用ツールチップ
    const onTitleTooltipShow = (bvEvent: Event): void => {
      const target = vm?.$refs["message-title"];
      if (target instanceof HTMLElement) {
        // webkit-line-clamp で省略された時、offsetHeight < scrollHeigt になる
        const oHeight = target.offsetHeight;
        const sHeight = target.scrollHeight;
        if (oHeight < sHeight) return;
      }
      bvEvent.preventDefault(); // 出さない
    };

    // ユーザ名用ツールチップ
    const onUserTooltipShow = (bvEvent: Event): void => {
      const target = vm?.$refs["contributor-el"];
      if (target instanceof HTMLElement) {
        // webkit-line-clamp で省略された時、offsetHeight < scrollHeigt になる
        const oHeight = target.offsetHeight;
        const sHeight = target.scrollHeight;
        if (oHeight < sHeight) return;
      }
      bvEvent.preventDefault(); // 出さない
    };

    watch(
      () => props.baseSort,
      (): void => {
        sortValue.value = props.baseSort;
      }
    );

    // created 相当（props の一回スナップショット。リアクティブ追跡は不要）
    onBeforeMount(() => {
      if (props.search) {
        showCommentList.value = false;
      } // 検索時はコメント[閉]
      sortValue.value = props.baseSort;
    });

    return {
      showCommentList,
      sortValue,
      allow_attachment,
      classes,
      commentSource,
      titleLabel,
      contributor,
      contributorId,
      contributorIcon,
      timeStamp,
      contents,
      likeStamp,
      commentLink,
      domainId,
      getMessage,
      editable,
      deletable,
      starFlag,
      likeFlag,
      likeCount,
      commentAvailable,
      reactionAvailable,
      findUser,
      openPhotoStream,
      onEditMessage,
      onDeleteMessage,
      onCommentLinkClick,
      onUrlCopyMessage,
      onErrorModalEvent,
      onStarClick,
      onLikeClick,
      clickedLink,
      showFavoriteList,
      onTitleTooltipShow,
      onUserTooltipShow,
    };
  },
});
</script>
